import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Text from '@Text';
import {
  formatDate,
  formatAppointmentTime,
  shouldRenderContent,
} from './thankYouPage.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const CONFIRMATION_MESSAGE_HEADLINE = {
  ES: '¡Enhorabuena! Tu reserva de cita en un centro de GAES ha sido confirmada',
};

const CONFIRMATION_MESSAGE_CONTENT = {
  ES: 'A continuación encontrarás el resumen de tu cita.',
};

const APPOINTMENT_ID_MESSAGE = {
  ES: 'Tu identificación de cita',
};

const FAILURE_MESSAGE = {
  ES: 'Gracias por tu solicitud',
};

const UNABLE_TO_BOOK_APPOINTMENT_MESSAGE = {
  ES: 'Desafortunadamente, no pudimos reservar tu cita.',
};

const NO_WORRIES = {
  ES: 'No te preocupes, aún podemos ayudarte.',
};

const SOMEONE_WILL_CONTACT_YOU = {
  ES: 'Revisaremos tus datos y lo conectaremos con el proveedor más adecuado.',
};

const DATE = {
  ES: 'Fecha:',
};

const TIME = {
  ES: 'Hora:',
};

const DIRECTION = {
  ES: 'Dirección del centro:',
};

const AppointmentBookingSection = ({
  referenceNumberText,
  bookedAppointmentId,
  submissionId,
  selectedAppointment,
}) => {
  const theme = useTheme();
  const { localeCode } = useSelector((state) => state.formConfig);
  const { selectedLocation } = selectedAppointment;
  const { countryAlpha2 } = selectedLocation;

  const formattedAppointmentDate = formatDate({
    isoDateString: selectedAppointment?.selectedDate,
    countryAlpha2,
  });

  const formattedAppointmentTime = formatAppointmentTime({
    isoDateString: selectedAppointment?.selectedDate,
    locale: localeCode.replace('_', '-'),
  });

  const textExperimentName = useGetExperimentName({
    componentName: 'Text',
  });

  const appointmentBookingSuccessfulSection = (
    <>
      <Box marginBottom={theme.mvf.spacing.medium}>
        <Text
          variant="header"
          align="center"
          experimentName={textExperimentName}
        >
          {CONFIRMATION_MESSAGE_HEADLINE[countryAlpha2] || 'Congratulations!'}
        </Text>
      </Box>
      <Box marginBottom={theme.mvf.spacing.medium}>
        <Text variant="text" align="center" experimentName={textExperimentName}>
          {CONFIRMATION_MESSAGE_CONTENT[countryAlpha2] || ''}
        </Text>
      </Box>
      <Text variant="text" align="center" experimentName={textExperimentName}>
        {DATE[countryAlpha2] || 'Date:'}
      </Text>
      <Text
        variant="listItem"
        align="center"
        experimentName={textExperimentName}
      >
        {formattedAppointmentDate}
      </Text>
      <Text variant="text" align="center" experimentName={textExperimentName}>
        {TIME[countryAlpha2] || 'Time:'}
      </Text>
      <Text
        variant="listItem"
        align="center"
        experimentName={textExperimentName}
      >
        {formattedAppointmentTime}
      </Text>
      <Text variant="text" align="center" experimentName={textExperimentName}>
        {DIRECTION[countryAlpha2] || 'Address:'}
      </Text>
      <Text
        variant="listItem"
        align="center"
        experimentName={textExperimentName}
      >
        {selectedAppointment?.selectedLocation?.name}
      </Text>
      <Text
        variant="listItem"
        align="center"
        experimentName={textExperimentName}
      >
        {selectedAppointment?.selectedLocation?.address}
      </Text>
      <Text variant="text" align="center" experimentName={textExperimentName}>
        {APPOINTMENT_ID_MESSAGE[countryAlpha2] || 'Your appointment ID is'}{' '}
        <strong>{bookedAppointmentId}</strong>
      </Text>

      {shouldRenderContent(referenceNumberText) && (
        <Box
          marginTop={theme.mvf.spacing.tiny}
          fontWeight={theme.mvf.typography.weight.thick}
        >
          <Text variant="text" experimentName={textExperimentName}>
            {referenceNumberText} <strong>{submissionId}</strong>
          </Text>
        </Box>
      )}
    </>
  );

  const appointmentBookingNotSuccessfulSection = (
    <>
      <Box
        paddingTop={theme.mvf.spacing.large}
        marginBottom={theme.mvf.spacing.medium}
      >
        <Text variant="header" align="center">
          {FAILURE_MESSAGE[countryAlpha2] || 'Thank you for your request'}
        </Text>
      </Box>

      <Text variant="text">
        <strong>
          {UNABLE_TO_BOOK_APPOINTMENT_MESSAGE[countryAlpha2] ||
            'Unfortunately, we were unable to book your appointment'}
        </strong>
      </Text>
      <Text variant="text">
        <strong>
          {NO_WORRIES[countryAlpha2] || 'Do not worry we can still help'}
        </strong>
      </Text>
      <Text variant="text">
        {SOMEONE_WILL_CONTACT_YOU[countryAlpha2] ||
          'We will review your details and connect you to the most appropriate provider'}
      </Text>
      {shouldRenderContent(referenceNumberText) && (
        <Box
          marginTop={theme.mvf.spacing.tiny}
          fontWeight={theme.mvf.typography.weight.thick}
        >
          <Text variant="text">
            {referenceNumberText} <strong>{submissionId}</strong>
          </Text>
        </Box>
      )}
    </>
  );

  return bookedAppointmentId
    ? appointmentBookingSuccessfulSection
    : appointmentBookingNotSuccessfulSection;
};

export default AppointmentBookingSection;
